import React, { useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { formatFCFA, imageUrl } from "../lib/format";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { PackageSearch, Check, XCircle } from "lucide-react";

const STEPS = [
  { id: "en_attente_paiement", label: "Commande reçue" },
  { id: "paiement_confirme", label: "Paiement confirmé" },
  { id: "en_preparation", label: "En préparation" },
  { id: "expediee", label: "En route" },
  { id: "livree", label: "Livrée" },
];

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString("fr-FR", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "");

function Timeline({ order }) {
  if (order.statut === "annulee") {
    return <p className="flex items-center gap-2 rounded-xl bg-red-50 p-4 text-sm text-red-700" data-testid="track-cancelled"><XCircle className="h-5 w-5" /> Cette commande a été annulée. Contactez-nous sur WhatsApp pour toute question.</p>;
  }
  const current = STEPS.findIndex((s) => s.id === order.statut);
  const dates = {};
  (order.historique || []).forEach((h) => { dates[h.statut] = h.at; });
  return (
    <ol className="space-y-0" data-testid="track-timeline">
      {STEPS.map((s, i) => {
        const done = i <= current;
        return (
          <li key={s.id} className="relative flex gap-4 pb-6 last:pb-0" data-testid={`track-step-${s.id}`}>
            {i < STEPS.length - 1 && <span className={`absolute left-[15px] top-8 h-[calc(100%-2rem)] w-0.5 ${i < current ? "bg-[#D76E4D]" : "bg-[#E8D5CE]"}`} />}
            <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${done ? "bg-[#D76E4D] text-white" : "bg-white text-[#9B7E73] ring-1 ring-[#E8D5CE]"}`}>
              {done ? <Check className="h-4 w-4" /> : i + 1}
            </span>
            <div className="pt-1">
              <p className={`font-medium ${i === current ? "text-[#D76E4D]" : done ? "text-[#2C1A14]" : "text-[#9B7E73]"}`}>{s.label}</p>
              {done && dates[s.id] && <p className="text-xs text-[#9B7E73]">{fmtDate(dates[s.id])}</p>}
            </div>
          </li>
        );
      })}
    </ol>
  );
}

export default function Suivi() {
  const [params] = useSearchParams();
  const [numero, setNumero] = useState(params.get("numero") || "");
  const [tel, setTel] = useState(() => sessionStorage.getItem("yoyo_last_phone") || "");
  const [order, setOrder] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/orders/suivi", { numero, telephone: tel });
      setOrder(data);
    } catch (err) {
      setOrder(null);
      setError(err.response?.data?.detail || "Commande introuvable");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mx-auto max-w-2xl px-4 pb-28 pt-12 sm:px-6 lg:pb-16 lg:pt-16" data-testid="suivi-page">
      <PackageSearch className="h-10 w-10 text-[#D76E4D]" />
      <h1 className="mt-3 font-serif text-4xl font-medium text-[#2C1A14] sm:text-5xl">Suivre ma commande</h1>
      <p className="mt-2 text-sm text-[#6E544A]">Indiquez votre numéro de commande et le téléphone utilisé lors de l'achat.</p>
      <form onSubmit={submit} className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-[1fr_1fr_auto]">
        <Input value={numero} onChange={(e) => setNumero(e.target.value)} placeholder="N° de commande (ex : DY-0012)" required data-testid="track-numero" className="bg-white" />
        <Input value={tel} onChange={(e) => setTel(e.target.value)} placeholder="Téléphone" inputMode="tel" required data-testid="track-tel" className="bg-white" />
        <Button type="submit" disabled={loading} data-testid="track-submit" className="rounded-full bg-[#2C1A14] px-6 text-white hover:bg-[#D76E4D]">{loading ? "..." : "Suivre"}</Button>
      </form>
      {error && <p className="mt-4 text-sm text-red-600" data-testid="track-error">{error}</p>}

      {order && (
        <div className="mt-8 rounded-2xl bg-white p-6 ring-1 ring-[#E8D5CE]" data-testid="track-result">
          <div className="mb-6 flex flex-wrap items-baseline justify-between gap-2">
            <p className="font-serif text-xl text-[#2C1A14]">Commande <strong>{order.numero}</strong></p>
            <p className="text-sm text-[#9B7E73]">Passée le {fmtDate(order.created_at)}</p>
          </div>
          <Timeline order={order} />
          <div className="mt-6 space-y-2 border-t border-[#F0E4DE] pt-4">
            {order.items.map((it, i) => (
              <div key={it.product_id || i} className="flex items-center gap-3 text-sm">
                <img src={imageUrl(it.image, 320)} alt="" className="h-12 w-10 rounded object-cover" />
                <span className="flex-1 text-[#2C1A14]">{it.nom}</span>
                <span className="text-[#6E544A]">{formatFCFA(it.prix_unitaire * it.quantite)}</span>
              </div>
            ))}
            <div className="flex justify-between pt-2 font-semibold text-[#2C1A14]"><span>Total</span><span>{formatFCFA(order.total)}</span></div>
          </div>
        </div>
      )}
    </div>
  );
}
